import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { useQueryState, parseAsIndex } from "nuqs";
import { t } from "@lingui/core/macro";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { DataTable } from "@/components/table/DataTable.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { Loader } from "@/components/Loader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { columnLabelsMap, domesticColumns } from "@/modules/domesticPayments/TableColumnsDef.tsx";
import {
  type DomesticTransactionsFilter,
  useDomesticTransactionsQuery,
} from "@/modules/domesticPayments/api/useDomesticTransactions.ts";
import { getCurrenciesOptions } from "@/modules/domesticPayments/utils.ts";
import {
  getFilterValue,
  getItemPerPage,
  getSortingValue,
  getVisibilityStateValue,
  setFilterValue,
  setItemPerPage,
  setVisibilityStateValue,
  sortingParser,
} from "@/lib/table.ts";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { FilterProvider, type FilterProviderOnChange } from "@/components/table/FilterProvider.tsx";
import { Export } from "@/modules/domesticPayments/Export.tsx";
import { AllBankDataProvider } from "@/dataProviders/BankDataProvider.tsx";

const DOMESTIC_FILTERS_KEY = "@fin_merch_domestic_filters";
const DOMESTIC_COLUMN_VISIBILITY_KEY = "@fin_merch_domestic_visible_columns";
export const DOMESTIC_LOCATION_KEY = "@fin_merch_domestic_location";
export const Route = createFileRoute("/payments/domestic/")({
  component: RouteComponent,
});

function RouteComponent() {
  const title = t`Domestic payments`;
  const { activeApp, currencies } = useAppConfig();

  const [sorting, setSorting] = useQueryState<SortingState>(
    "sort",
    sortingParser.withDefault([{ id: "createdAt", desc: true }]),
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getVisibilityStateValue(DOMESTIC_COLUMN_VISIBILITY_KEY),
  );

  useEffect(() => {
    setVisibilityStateValue(DOMESTIC_COLUMN_VISIBILITY_KEY, columnVisibility);
  }, [columnVisibility]);

  const [filter, setFilter] = useState<DomesticTransactionsFilter>(getFilterValue(DOMESTIC_FILTERS_KEY));
  const onFilterChange: FilterProviderOnChange = (values) => {
    setFilter((prevState) => ({ ...prevState, ...values }));
    setFilterValue(DOMESTIC_FILTERS_KEY, { ...filter, ...values });
  };

  const [page, setPage] = useQueryState<number>("page", parseAsIndex.withDefault(0));
  const [perPage, setPerPage] = useState<number>(getItemPerPage());

  const pagination: PaginationState = { pageIndex: page, pageSize: perPage };
  const setPagination: OnChangeFn<PaginationState> = (value) => {
    if ("pageIndex" in value) return;

    const { pageIndex, pageSize } = value(pagination);
    void setPage(pageIndex);
    setPerPage(pageSize);
    setItemPerPage(pageSize);
  };

  const { data, isPending, isError, error } = useDomesticTransactionsQuery(
    {
      pageNumber: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: getSortingValue(sorting ?? []),
      ...filter,
    },
    { merchantId: activeApp },
    { enabled: !!activeApp, placeholderData: keepPreviousData },
  );

  const columnsConfig = useMemo(() => domesticColumns(getCurrenciesOptions(currencies)), [currencies]);

  const table = useReactTable({
    data: data?.items ?? [],
    columns: columnsConfig,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.pagination?.totalItemsCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: { sorting, columnVisibility, pagination },
  });

  const labels = useMemo(columnLabelsMap, []);

  return (
    <AllBankDataProvider>
      <FilterProvider value={filter} onChange={onFilterChange}>
        <PageWrapper>
          <PageHeader breadcrumbs={[{ href: "/", title }]} />
          <div className="flex flex-col grow p-5">
            {isError && (
              <FetchingError
                message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
                error={error}
              />
            )}
            {isPending && <Loader message={t`Loading domestic payments...`} />}
            {!isPending && !isError && (
              <DataTable
                table={table}
                labels={labels}
                detailRoute={"/payments/domestic/$id"}
                detailIdKey={"merchantTransactionId"}
                exportComponent={<Export table={table} filter={filter} />}
                locationKey={DOMESTIC_LOCATION_KEY}
              />
            )}
          </div>
        </PageWrapper>
      </FilterProvider>
    </AllBankDataProvider>
  );
}
